import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AdminShell from "@/pages/admin/AdminShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  LifeBuoy,
  Search,
  Send,
  Loader2,
  ArrowLeft,
  Mail,
  User as UserIcon,
  Tag,
  Clock,
  CheckCircle2,
  MessageSquare,
  Download,
} from "lucide-react";

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  closed: "Closed",
};

const CATEGORY_LABELS = {
  account: "Account",
  kyc: "KYC",
  deposit: "Deposits",
  withdrawal: "Withdrawals",
  transfer: "Transfers",
  crypto: "Crypto",
  security: "Security",
  billing: "Billing",
  other: "Other",
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, closed: 0 });
  const [status, setStatus] = useState("open");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/support/admin/tickets?status=${status}&category=${category}&q=${encodeURIComponent(q)}`
      );
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, category]);

  return (
    <AdminShell>
      <div className="text-eyebrow text-[#4F4D45]">Support console</div>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-1">
        <div className="flex items-center gap-3">
          <LifeBuoy className="w-6 h-6 text-[#1B3526]" />
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Tickets
          </h1>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/tickets.csv?status=${status}&category=${category}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B3526] hover:bg-[#0F2517] text-white text-sm font-medium"
          data-testid="admin-tickets-export-csv"
        >
          <Download className="w-4 h-4" /> Export CSV
        </a>
      </div>

      {/* Status tabs */}
      <div className="mt-8 flex flex-wrap gap-2">
        {(["open", "in_progress", "closed", "all"]).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`admin-tickets-tab-${s}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              status === s
                ? "bg-[#1B3526] text-white"
                : "bg-white border border-[#D2CDBE] text-[#0A0A08] hover:bg-[#F0ECE0]"
            }`}
          >
            {s === "all" ? "All" : STATUS_LABELS[s]}{" "}
            {s !== "all" && (
              <span className="ml-1 text-xs opacity-80">({counts[s] || 0})</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4F4D45]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by ticket ID, name, email, or content…"
            className="pl-9 bg-white"
            data-testid="admin-tickets-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-56 bg-white" data-testid="admin-tickets-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={load}
          className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
          data-testid="admin-tickets-refresh"
        >
          Refresh
        </Button>
      </div>

      {/* List */}
      <div className="mt-6 rounded-2xl border border-[#D2CDBE] bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#4F4D45]">Loading…</div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-[#4F4D45]">
            No tickets to show.
          </div>
        ) : (
          <ul className="divide-y divide-[#D2CDBE]" data-testid="admin-tickets-list">
            {tickets.map((t) => (
              <li
                key={t.ticket_id}
                data-testid={`admin-ticket-item-${t.ticket_id}`}
              >
                <Link
                  to={`/admin/tickets/${t.ticket_id}`}
                  className="grid grid-cols-12 gap-4 px-5 py-4 hover:bg-[#F0ECE0]/60"
                >
                  <div className="col-span-3">
                    <div className="text-sm font-semibold text-[#0A0A08] truncate">
                      {t.name}
                    </div>
                    <div className="text-xs text-[#4F4D45] truncate">
                      {t.email}
                    </div>
                    <div className="text-[10px] font-mono text-[#4F4D45] mt-1">
                      {t.ticket_id}
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="text-sm text-[#0A0A08] font-medium truncate">
                      {t.subject}
                    </div>
                    <div className="text-xs text-[#4F4D45] mt-1 line-clamp-1">
                      {t.message}
                    </div>
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-3">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-[#F0ECE0] text-[#1B3526]"
                      data-testid={`admin-ticket-category-${t.ticket_id}`}
                    >
                      <Tag className="w-3 h-3" />
                      {CATEGORY_LABELS[t.category] || t.category}
                    </span>
                    <StatusPill status={t.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminShell>
  );
}

function StatusPill({ status }) {
  const map = {
    open: "bg-[#B58957]/15 text-[#B58957]",
    in_progress: "bg-[#1B3526]/10 text-[#1B3526]",
    closed: "bg-[#D2CDBE] text-[#4F4D45]",
  };
  const Icon =
    status === "closed" ? CheckCircle2 : status === "in_progress" ? Clock : Mail;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
        map[status] || map.open
      }`}
    >
      <Icon className="w-3 h-3" />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function AdminTicketDetail() {
  const { ticket_id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [closeAfterReply, setCloseAfterReply] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/support/admin/tickets/${ticket_id}`);
      setTicket(data);
      setAdminNotes(data.admin_notes || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket_id]);

  const setStatus = async (newStatus) => {
    try {
      const { data } = await api.patch(`/support/admin/tickets/${ticket_id}`, {
        status: newStatus,
      });
      setTicket(data);
      toast.success(`Status: ${STATUS_LABELS[newStatus]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/support/admin/tickets/${ticket_id}`, {
        admin_notes: adminNotes,
      });
      setTicket(data);
      toast.success("Notes saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendReply = async () => {
    if (reply.trim().length < 1) {
      toast.error("Write a reply first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(
        `/support/admin/tickets/${ticket_id}/reply`,
        { message: reply, close: closeAfterReply }
      );
      setTicket(data.ticket);
      setReply("");
      setCloseAfterReply(false);
      if (data.delivered) toast.success("Reply sent.");
      else toast.warning("Reply saved but email delivery failed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!ticket) {
    return (
      <AdminShell>
        <div className="text-[#4F4D45] py-16 text-center">Loading…</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <Link
        to="/admin/tickets"
        className="text-sm text-[#4F4D45] hover:text-[#1B3526] inline-flex items-center gap-1.5 mb-4"
        data-testid="admin-ticket-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to tickets
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">
            {ticket.ticket_id} · {CATEGORY_LABELS[ticket.category] || ticket.category}
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">
            {ticket.subject}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={ticket.status} />
          <Select value={ticket.status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 bg-white" data-testid="admin-ticket-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Original message */}
          <div className="rounded-2xl border border-[#D2CDBE] bg-white p-6">
            <div className="flex items-center gap-2 text-sm text-[#4F4D45]">
              <UserIcon className="w-4 h-4" />
              <span className="font-semibold text-[#0A0A08]">{ticket.name}</span>
              <span>&lt;{ticket.email}&gt;</span>
              <span className="ml-auto text-xs">
                {ticket.created_at
                  ? new Date(ticket.created_at).toLocaleString()
                  : ""}
              </span>
            </div>
            <div
              className="mt-4 whitespace-pre-wrap text-[#0A0A08] leading-relaxed border-l-2 border-[#B58957] pl-4"
              data-testid="admin-ticket-message"
            >
              {ticket.message}
            </div>
          </div>

          {/* Replies */}
          {(ticket.replies || []).map((r, i) => (
            <div
              key={i}
              className="rounded-2xl border border-[#1B3526]/20 bg-[#F0ECE0]/60 p-6"
              data-testid={`admin-ticket-reply-${i}`}
            >
              <div className="flex items-center gap-2 text-sm text-[#4F4D45]">
                <MessageSquare className="w-4 h-4 text-[#1B3526]" />
                <span className="font-semibold text-[#0A0A08]">
                  {r.from}
                </span>
                <span className="ml-auto text-xs">
                  {r.sent_at ? new Date(r.sent_at).toLocaleString() : ""}
                  {!r.delivered && (
                    <span className="ml-2 text-[#E27A6A]">· delivery failed</span>
                  )}
                </span>
              </div>
              <div className="mt-4 whitespace-pre-wrap text-[#0A0A08] leading-relaxed">
                {r.message}
              </div>
            </div>
          ))}

          {/* Reply composer */}
          <div className="rounded-2xl border border-[#D2CDBE] bg-white p-6">
            <div className="font-display font-semibold text-[#0A0A08]">
              Send a reply
            </div>
            <p className="text-xs text-[#4F4D45] mt-1">
              This goes to {ticket.email} from FinVault Support. Reply-To is set
              to support@finvault.capital.
            </p>
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write your reply…"
              className="mt-3 min-h-[160px]"
              maxLength={5000}
              data-testid="admin-ticket-reply-input"
            />
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-[#4F4D45]">
                <input
                  type="checkbox"
                  checked={closeAfterReply}
                  onChange={(e) => setCloseAfterReply(e.target.checked)}
                  data-testid="admin-ticket-close-after"
                />
                Close ticket after sending
              </label>
              <Button
                onClick={sendReply}
                disabled={sending}
                className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
                data-testid="admin-ticket-send-reply"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send reply
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-[#D2CDBE] bg-white p-5">
            <div className="font-display font-semibold text-[#0A0A08]">
              Ticket details
            </div>
            <dl className="mt-3 space-y-2 text-sm">
              <Row label="ID">
                <span className="font-mono text-[#1B3526]">
                  {ticket.ticket_id}
                </span>
              </Row>
              <Row label="Category">
                {CATEGORY_LABELS[ticket.category] || ticket.category}
              </Row>
              <Row label="Status">
                {STATUS_LABELS[ticket.status] || ticket.status}
              </Row>
              <Row label="From">{ticket.name}</Row>
              <Row label="Email">{ticket.email}</Row>
              {ticket.user_id && (
                <Row label="User ID">
                  <span className="font-mono text-xs">{ticket.user_id}</span>
                </Row>
              )}
              <Row label="Created">
                {ticket.created_at
                  ? new Date(ticket.created_at).toLocaleString()
                  : "—"}
              </Row>
              {ticket.updated_at && (
                <Row label="Updated">
                  {new Date(ticket.updated_at).toLocaleString()}
                </Row>
              )}
            </dl>
          </div>

          <div className="rounded-2xl border border-[#D2CDBE] bg-white p-5">
            <div className="font-display font-semibold text-[#0A0A08]">
              Internal notes
            </div>
            <p className="text-xs text-[#4F4D45] mt-1">
              Only visible to admins.
            </p>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="mt-3 min-h-[120px]"
              placeholder="Investigation notes, findings, next steps…"
              data-testid="admin-ticket-notes"
            />
            <Button
              onClick={saveNotes}
              className="mt-3 w-full bg-white border border-[#D2CDBE] text-[#0A0A08] hover:bg-[#F0ECE0]"
              data-testid="admin-ticket-save-notes"
            >
              Save notes
            </Button>
          </div>
        </aside>
      </div>
    </AdminShell>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-xs uppercase tracking-wider text-[#4F4D45] w-20 shrink-0">
        {label}
      </dt>
      <dd className="text-[#0A0A08] flex-1 min-w-0 truncate">{children}</dd>
    </div>
  );
}
